import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { CARD } from "@/lib/ui";
import { RotateCcw, Trash2, Archive } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("el-GR", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function Trash() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user?.role === "owner";
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["trash"], queryFn: async () => (await api.get("/trash")).data,
  });
  const refresh = () => qc.invalidateQueries();

  const restore = useMutation({
    mutationFn: async (id) => (await api.post(`/trash/${id}/restore`)).data,
    onSuccess: () => { refresh(); toast.success("Η εγγραφή επανήλθε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const purge = useMutation({
    mutationFn: async (id) => (await api.delete(`/trash/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Διαγράφηκε οριστικά"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const empty = useMutation({
    mutationFn: async () => (await api.delete(`/trash?older_than_days=30`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["trash"] }); toast.success(d.purged ? `Αδειάστηκαν ${d.purged} εγγραφές (>30 ημερών)` : "Δεν υπάρχει τίποτα πάνω από 30 ημέρες"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const old = rows.filter((r) => Date.now() - new Date(r.deleted_at).getTime() > 30 * 86400000).length;

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Ασφάλεια δεδομένων</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Κάδος Ανακύκλωσης</h1>
        <p className="text-[#6F6A60] text-sm mt-1">
          Ό,τι διαγράφεται (χρήστες, μισθοδοσίες, tips, προγράμματα, κρατήσεις, δάνεια, τιμολόγια) φυλάσσεται εδώ και
          μπορεί να επανέλθει. Οριστική διαγραφή μόνο από τον ιδιοκτήτη.
        </p>
      </div>
      {isOwner && rows.length > 0 && (
        <button data-testid="trash-empty-btn" disabled={empty.isPending || !old}
          onClick={() => window.confirm(`Άδειασμα κάδου: οριστική διαγραφή ${old} εγγραφών παλαιότερων των 30 ημερών;\n\nΚρατείται αντίγραφο ασφαλείας (επαναφορά από Ιστορικό Αλλαγών).`) && empty.mutate()}
          className="inline-flex items-center gap-2 rounded-xl border border-[#991B1B] text-[#991B1B] px-4 py-2.5 text-sm font-semibold hover:bg-[#FEF2F2] disabled:opacity-50">
          <Trash2 className="h-4 w-4" /> Άδειασμα (&gt;30 ημέρες · {old})
        </button>
      )}
      </div>

      <div className={`${CARD} overflow-hidden`}>
        {isLoading ? (
          <p className="p-6 text-sm text-[#6F6A60]">Φόρτωση…</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-[#6F6A60] flex flex-col items-center gap-2" data-testid="trash-empty">
            <Archive className="h-6 w-6 text-[#B9AE9A]" /> Ο κάδος είναι άδειος.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                  <th className="px-4 py-2.5">Τύπος</th>
                  <th className="px-4 py-2.5">Εγγραφή</th>
                  <th className="px-4 py-2.5">Διαγράφηκε</th>
                  <th className="px-4 py-2.5">Από</th>
                  <th className="px-4 py-2.5 text-right">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#E3DED4]" data-testid={`trash-row-${r.id}`}>
                    <td className="px-4 py-2.5 text-[#6F6A60]">{r.collection_label}</td>
                    <td className="px-4 py-2.5 font-semibold text-[#45423C]">{r.title}</td>
                    <td className="px-4 py-2.5 text-[#6F6A60] tabular">{fmt(r.deleted_at)}</td>
                    <td className="px-4 py-2.5 text-[#6F6A60]">{r.deleted_by || "—"}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`trash-restore-${r.id}`} onClick={() => restore.mutate(r.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[#E3DED4] bg-white px-3 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A]">
                          <RotateCcw className="h-3.5 w-3.5" /> Επαναφορά
                        </button>
                        {isOwner && (
                          <button data-testid={`trash-purge-${r.id}`}
                            onClick={() => window.confirm("Οριστική διαγραφή; Δεν μπορεί να αναιρεθεί.") && purge.mutate(r.id)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#991B1B]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
                            <Trash2 className="h-3.5 w-3.5" /> Οριστικά
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
